import { supabase, isSupabaseConfigured as supabaseConfigured } from "@/lib/supabase";
import heic2any from "heic2any";
import {
  createTeacherAccount,
  findAccount,
  fileToDataUrl,
  saveSessionUser,
  saveStudentRecord,
  saveTimetableForClass,
  saveSingleFileForClass,
  saveHomeworkForClass,
  saveRemarkForStudent,
  saveConsentFormForClass,
  saveConsentSubmissionForClass,
  saveResultPdf,
  classKey,
} from "@/lib/eisStore";

function requireSupabase() {
  if (!supabaseConfigured || !supabase) {
    throw new Error("Supabase is not configured");
  }
  return supabase;
}

export const isSupabaseConfigured = supabaseConfigured;
const STORAGE_BUCKET_CANDIDATES = ["class-files", "timetables", "documents", "uploads", "files"];
let cachedStorageBucket = "";
let cachedStorageBucketPromise = null;

export async function resolveStorageBucket(preferredBucket = "") {
  if (cachedStorageBucket) {
    return cachedStorageBucket;
  }
  if (cachedStorageBucketPromise) {
    return cachedStorageBucketPromise;
  }

  cachedStorageBucketPromise = (async () => {
    const client = requireSupabase();
    try {
      const { data, error } = await client.storage.listBuckets();
      if (error) throw error;
      const available = (data || []).map((bucket) => bucket.name);
      const preferredOrder = [
        preferredBucket,
        ...STORAGE_BUCKET_CANDIDATES,
        ...(available || []),
      ].filter(Boolean);
      const found = preferredOrder.find((bucketName, index) => {
        if (index === 0 && preferredBucket) {
          return available.includes(preferredBucket);
        }
        return available.includes(bucketName);
      });
      cachedStorageBucket = found || available[0] || preferredBucket || STORAGE_BUCKET_CANDIDATES[0];
    } catch (error) {
      console.error(error);
      cachedStorageBucket = preferredBucket || STORAGE_BUCKET_CANDIDATES[0];
    } finally {
      cachedStorageBucketPromise = null;
    }
    return cachedStorageBucket;
  })();

  return cachedStorageBucketPromise;
}

export function decodeClassKey(classKeyValue = "") {
  const [standard = "", division = ""] = classKeyValue.split("__");
  return {
    standard: standard.replace(/-/g, " "),
    division: division.replace(/-/g, " "),
  };
}

export async function getSupabaseCurrentUserId() {
  const client = requireSupabase();
  const { data, error } = await client.auth.getUser();
  if (error) throw error;
  return data?.user?.id || null;
}

async function fileToRenderableDataUrl(file) {
  const isHeic =
    file?.type === "image/heic" ||
    file?.type === "image/heif" ||
    /\.hei(c|f)$/i.test(file?.name || "");

  if (!isHeic) {
    return fileToDataUrl(file);
  }

  const converted = await heic2any({
    blob: file,
    toType: "image/jpeg",
    quality: 0.92,
  });

  const jpegBlob = Array.isArray(converted) ? converted[0] : converted;
  if (!jpegBlob) {
    throw new Error("Could not convert HEIC image");
  }
  return fileToDataUrl(jpegBlob);
}

export async function signUpTeacher({ email, password, name, standard, division }) {
  const client = requireSupabase();
  if (!standard) division = null;

  const { data, error } = await client.auth.signUp({
    email,
    password,
    options: {
      data: {
        name,
        role: "teacher",
        standard: standard || null,
        division: division || null,
      },
    },
  });
  if (error) throw error;
  return data;
}

export async function signUpStudent({ email, password, name, standard, division }) {
  const client = requireSupabase();

  const { data, error } = await client.auth.signUp({
    email,
    password,
    options: {
      data: {
        name,
        standard,
        division,
        role: "student",
      },
    },
  });
  if (error) throw error;
  return data;
}

export async function checkProfileLinked(userId = "") {
  const client = requireSupabase();
  let resolvedUserId = userId;
  if (!resolvedUserId) {
    const {
      data: { user },
      error: userError,
    } = await client.auth.getUser();
    if (userError) throw userError;
    resolvedUserId = user?.id || "";
  }
  if (!resolvedUserId) return false;

  const { data, error } = await client
    .from("students")
    .select("id")
    .eq("user_id", resolvedUserId)
    .maybeSingle();
  if (error) throw error;
  return Boolean(data);
}

export async function signInTeacher({ email, password }) {
  const client = requireSupabase();
  const { data, error } = await client.auth.signInWithPassword({
    email,
    password,
  });
  if (error) throw error;
  return data;
}

export async function signInStudent({ email, password }) {
  const client = requireSupabase();
  const { data, error } = await client.auth.signInWithPassword({
    email,
    password,
  });
  if (error) throw error;
  return data;
}

export async function signOutCurrentUser() {
  const client = requireSupabase();
  const { error } = await client.auth.signOut();
  if (error) throw error;
}

export async function getTeacherProfileById(id) {
  const client = requireSupabase();
  const { data, error } = await client
    .from("teachers")
    .select("id,name,email,standard,division")
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

export async function updateTeacherProfile(teacherId, updates) {
  const client = requireSupabase();
  const sanitizedUpdates = { ...updates };
  const classKeyValue = sanitizedUpdates.classKey;
  delete sanitizedUpdates.id;
  delete sanitizedUpdates.supabaseUserId;
  delete sanitizedUpdates.role;
  delete sanitizedUpdates.password;
  delete sanitizedUpdates.classKey;
  delete sanitizedUpdates.studentId;
  delete sanitizedUpdates.accountStatus;

  if (classKeyValue) {
    const { standard, division } = decodeClassKey(classKeyValue);
    sanitizedUpdates.standard = standard || sanitizedUpdates.standard || "";
    sanitizedUpdates.division = division || sanitizedUpdates.division || "";
  }

  const { error } = await client.from("teachers").update(sanitizedUpdates).eq("id", teacherId);
  if (error) throw error;
}

export async function updateTeacherClassInSupabase(teacherId, classKeyValue) {
  const client = requireSupabase();
  const { standard, division } = decodeClassKey(classKeyValue);
  const { error } = await client
    .from("teachers")
    .update({
      standard,
      division,
    })
    .eq("id", teacherId);
  if (error) throw error;
}

export async function getStudentProfileById(id) {
  const client = requireSupabase();
  const { data, error } = await client
    .from("students")
    .select("*")
    .eq("user_id", id)
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

export async function getAdminProfile() {
  const client = requireSupabase();
  const {
    data: { user },
    error: userError,
  } = await client.auth.getUser();
  if (userError) throw userError;
  if (!user?.id) {
    throw new Error("No authenticated admin found");
  }

  const { data, error } = await client
    .from("admins")
    .select("*")
    .eq("id", user.id)
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

export async function updateAdminContact({ phoneNumber, address }) {
  const client = requireSupabase();
  const {
    data: { user },
    error: userError,
  } = await client.auth.getUser();
  if (userError) throw userError;
  if (!user?.id) {
    throw new Error("No authenticated admin found");
  }

  const { error } = await client
    .from("admins")
    .update({
      phone_number: phoneNumber,
      address,
    })
    .eq("id", user.id);
  if (error) throw error;
}

export async function addStudent(studentData) {
  const client = requireSupabase();
  const { data: userData, error: userError } = await client.auth.getUser();
  if (userError) throw userError;

  const { data, error } = await client.from("students").insert({
    student_name: studentData.studentName,
    mother_name: studentData.motherName,
    father_name: studentData.fatherName,
    phone_number: studentData.phoneNumber,
    roll_number: studentData.rollNumber,
    standard: studentData.standard,
    division: studentData.division,
    mode_of_transport: studentData.modeOfTransport,
    address: studentData.address,
    created_by: userData?.user?.id,
  });
  if (error) throw error;

  return data;
}

function normalizeStudentRow(row = {}) {
  const standard = row.standard || "";
  const division = row.division || "";
  const name = row.student_name || row.studentName || row.name || "";
  return {
    id: row.id || "",
    studentId: row.id || "",
    name,
    studentName: name,
    motherName: row.mother_name || row.motherName || "",
    fatherName: row.father_name || row.fatherName || "",
    phone: row.phone_number || row.phone || "",
    rollNo: row.roll_number || row.rollNo || "",
    standard,
    division,
    transportMode: row.mode_of_transport || row.transportMode || "",
    address: row.address || "",
    email: row.email || "",
    accountStatus: row.email ? "active" : "pending",
    classKey: standard && division ? classKey(standard, division) : "",
    userId: row.user_id || "",
    createdBy: row.created_by || "",
  };
}

export async function getAllStudents() {
  const client = requireSupabase();
  const { data, error } = await client.from("students").select("*");
  if (error) throw error;
  return (data || []).map(normalizeStudentRow);
}

export async function getStudentsForClassFromSupabase(targetClassKey) {
  const client = requireSupabase();
  const [standard = "", division = ""] = String(targetClassKey || "").split("__");
  const { data, error } = await client
    .from("students")
    .select("*")
    .eq("standard", standard)
    .eq("division", division);
  if (error) throw error;
  return (data || []).map(normalizeStudentRow);
}

export async function updateStudent(studentId, updates) {
  const client = requireSupabase();
  const sanitizedUpdates = { ...updates };
  delete sanitizedUpdates.email;

  const { error } = await client.from("students").update(sanitizedUpdates).eq("id", studentId);
  if (error) throw error;
}

export async function updateStudentInSupabase(studentId, updates) {
  const client = requireSupabase();
  const sanitizedUpdates = {
    student_name: updates.studentName || updates.name,
    mother_name: updates.motherName,
    father_name: updates.fatherName,
    phone_number: updates.phone,
    roll_number: updates.rollNo,
    standard: updates.standard,
    division: updates.division,
    mode_of_transport: updates.transportMode,
    address: updates.address,
    email: updates.email,
    user_id: updates.userId,
  };

  Object.keys(sanitizedUpdates).forEach((key) => {
    if (sanitizedUpdates[key] === undefined) {
      delete sanitizedUpdates[key];
    }
  });

  const { error } = await client.from("students").update(sanitizedUpdates).eq("id", studentId);
  if (error) throw error;
}

export async function deleteStudentFromSupabase(studentId) {
  const client = requireSupabase();
  const { error } = await client.from("students").delete().eq("id", studentId);
  if (error) throw error;
}

export async function uploadCircular(file, title) {
  const client = requireSupabase();
  const path = `${Date.now()}-${file.name}`;
  const bucket = await resolveStorageBucket("circulars");

  const { error: uploadError } = await client.storage.from(bucket).upload(path, file);
  if (uploadError) throw uploadError;

  const { error } = await client.from("circulars").insert({
    title,
    file_path: path,
    uploaded_by: (await client.auth.getUser()).data.user.id,
  });
  if (error) throw error;

  return { path };
}

export async function getCirculars() {
  const client = requireSupabase();
  const { data, error } = await client
    .from("circulars")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function updateDashboardImage(imageKey, file) {
  const client = requireSupabase();
  const path = `${imageKey}-${Date.now()}-${file.name}`;
  const bucket = await resolveStorageBucket("dashboard-media");

  const { error: uploadError } = await client.storage.from(bucket).upload(path, file);
  if (uploadError) throw uploadError;

  const { error } = await client.from("dashboard_media").upsert(
    {
      image_key: imageKey,
      file_path: path,
      updated_by: (await client.auth.getUser()).data.user.id,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "image_key" },
  );
  if (error) throw error;

  return { path };
}

export async function getDashboardImageUrl(imageKey) {
  const client = requireSupabase();
  const { data, error } = await client
    .from("dashboard_media")
    .select("file_path")
    .eq("image_key", imageKey)
    .maybeSingle();
  if (error) throw error;
  if (!data?.file_path) return null;

  const bucket = await resolveStorageBucket("dashboard-media");
  const { data: urlData, error: urlError } = await client.storage.from(bucket).getPublicUrl(data.file_path);
  if (urlError) throw urlError;
  return urlData?.publicUrl || null;
}

async function getLinkedStudentProfile() {
  const client = requireSupabase();
  const {
    data: { user },
    error: userError,
  } = await client.auth.getUser();
  if (userError) throw userError;
  if (!user?.id) {
    throw new Error("No authenticated student found");
  }

  const { data: profile, error } = await client
    .from("students")
    .select("*")
    .eq("user_id", user.id)
    .maybeSingle();
  if (error) throw error;
  if (!profile) {
    throw new Error("Profile not found, please check details or contact your teacher");
  }
  return { client, user, profile };
}

export async function getMyProfile() {
  const { profile } = await getLinkedStudentProfile();
  return profile;
}

export async function getMyAttendance() {
  const { client, profile } = await getLinkedStudentProfile();
  const { data, error } = await client
    .from("attendance")
    .select("date, status")
    .eq("student_id", profile.id)
    .order("date", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getMyDocuments(category) {
  const { client, profile } = await getLinkedStudentProfile();
  const { data, error } = await client
    .from("documents")
    .select("*")
    .eq("standard", profile.standard)
    .eq("division", profile.division)
    .eq("category", category);
  if (error) throw error;
  return data || [];
}

export async function getLatestDocumentForClass(standard, division, category) {
  const client = requireSupabase();
  const { data, error } = await client
    .from("documents")
    .select("*")
    .eq("standard", standard)
    .eq("division", division)
    .eq("category", category)
    .order("created_at", { ascending: false })
    .limit(1);
  if (error) throw error;
  return data?.[0] || null;
}

export async function submitConsentForm(file) {
  const { client, profile } = await getLinkedStudentProfile();
  const path = `${profile.id}/consent-${Date.now()}-${file.name}`;
  const bucket = await resolveStorageBucket("student-uploads");

  const { error: uploadError } = await client.storage.from(bucket).upload(path, file);
  if (uploadError) throw uploadError;

  const { error } = await client.from("consent_form_submissions").insert({
    student_id: profile.id,
    file_path: path,
  });
  if (error) throw error;

  return { path };
}

export async function getMyRemarks() {
  const { client, profile } = await getLinkedStudentProfile();
  const { data, error } = await client
    .from("remarks")
    .select("remark, remark_type, created_at")
    .eq("student_id", profile.id)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function updateStudentProfile(studentId, updates) {
  const client = requireSupabase();
  const sanitizedUpdates = { ...updates };
  delete sanitizedUpdates.email;
  const { error } = await client.from("students").update(sanitizedUpdates).eq("id", studentId);
  if (error) throw error;
}

export async function uploadTimetable(file, standard, division) {
  const client = requireSupabase();
  const filePath = await fileToRenderableDataUrl(file);

  const { error: dbError } = await client.from("timetables").upsert(
    {
      standard,
      division,
      file_path: filePath,
      uploaded_by: (await client.auth.getUser()).data.user.id,
    },
    { onConflict: "standard,division" },
  );
  if (dbError) throw dbError;

  return { path: filePath };
}

export async function getTimetableRecordByClass(standard, division) {
  const client = requireSupabase();
  const { data, error } = await client
    .from("timetables")
    .select("standard,division,file_path,uploaded_at")
    .eq("standard", standard)
    .eq("division", division)
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

export async function uploadDocument(file, standard, division, category, title) {
  const client = requireSupabase();
  const path = `${standard}-${division}/${category}/${Date.now()}-${file.name}`;
  const bucket = await resolveStorageBucket("class-files");

  const { error: uploadError } = await client.storage
    .from(bucket)
    .upload(path, file);
  if (uploadError) throw uploadError;

  const { error: dbError } = await client.from("documents").insert({
    standard,
    division,
    category,
    title,
    file_path: path,
    uploaded_by: (await client.auth.getUser()).data.user.id,
  });
  if (dbError) throw dbError;

  return { path };
}

export async function addHomework({ standard, division, subject, description, dueDate, file }) {
  const client = requireSupabase();
  let filePath = null;

  if (file) {
    filePath = `${standard}-${division}/homework/${Date.now()}-${file.name}`;
    const bucket = await resolveStorageBucket("class-files");
    const { error: uploadError } = await client.storage
      .from(bucket)
      .upload(filePath, file);
    if (uploadError) throw uploadError;
  }

  const { error } = await client.from("homework").insert({
    standard,
    division,
    subject,
    description,
    due_date: dueDate,
    file_path: filePath,
    uploaded_by: (await client.auth.getUser()).data.user.id,
  });
  if (error) throw error;

  return { filePath };
}

export async function addRemark(studentId, remarkText, remarkType = "neutral") {
  const client = requireSupabase();
  const { error } = await client.from("remarks").insert({
    student_id: studentId,
    remark: remarkText,
    remark_type: remarkType,
    given_by: (await client.auth.getUser()).data.user.id,
  });
  if (error) throw error;
}

export async function getFileUrl(bucket, path) {
  const client = requireSupabase();
  if (typeof path === "string" && path.startsWith("data:")) {
    return path;
  }
  const resolvedBucket = await resolveStorageBucket(bucket);
  const { data, error } = await client.storage.from(resolvedBucket).createSignedUrl(path, 60 * 60);
  if (error) throw error;
  return data.signedUrl;
}

export async function saveTeacherSessionFromSupabase({ authUser, profile }) {
  if (!authUser) return null;
  const localTeacher = findAccount({ role: "teacher", email: authUser.email || "" });
  const assignedClassKey =
    (profile?.standard && profile?.division ? classKey(profile.standard, profile.division) : "") ||
    localTeacher?.assignedClassKey ||
    "";
  const account = createTeacherAccount({
    id: authUser.id,
    name: profile?.name || localTeacher?.name || authUser.email || "",
    email: authUser.email || "",
    password: "",
    assignedClassKey,
  });
  saveSessionUser({
    ...account,
    supabaseUserId: authUser.id,
  });
  return account;
}

export async function saveStudentSessionFromSupabase({ authUser, profile }) {
  if (!authUser) return null;

  const standard = profile?.standard || authUser.user_metadata?.standard || "";
  const division = profile?.division || authUser.user_metadata?.division || "";
  const name =
    profile?.student_name || profile?.name || authUser.user_metadata?.name || authUser.email || "";
  const email = profile?.email || authUser.email || "";

  saveStudentRecord({
    id: profile?.id || authUser.id,
    name,
    studentName: name,
    motherName: profile?.mother_name || "",
    fatherName: profile?.father_name || "",
    phone: profile?.phone_number || profile?.phone || "",
    rollNo: profile?.roll_number || profile?.rollNo || "",
    standard,
    division,
    transportMode: profile?.mode_of_transport || profile?.transportMode || "",
    address: profile?.address || "",
    email,
  });

  const account = {
    id: profile?.id || authUser.id,
    studentId: profile?.id || authUser.id,
    role: "student",
    name,
    studentName: name,
    motherName: profile?.mother_name || "",
    fatherName: profile?.father_name || "",
    phone: profile?.phone_number || profile?.phone || "",
    rollNo: profile?.roll_number || profile?.rollNo || "",
    standard,
    division,
    transportMode: profile?.mode_of_transport || profile?.transportMode || "",
    address: profile?.address || "",
    email,
    accountStatus: "active",
    classKey:
      standard && division ? classKey(standard, division) : "",
    supabaseUserId: authUser.id,
  };

  saveSessionUser(account);
  return account;
}

export {
  saveStudentRecord,
  saveTimetableForClass,
  saveSingleFileForClass,
  saveHomeworkForClass,
  saveRemarkForStudent,
  saveConsentFormForClass,
  saveConsentSubmissionForClass,
  saveResultPdf,
};
